import { Alert } from '@risk-smart/themed-cloudscape-components';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useDeleteInternalAuditReports } from 'src/hooks/mutations/internal-audit-report';
import {
  useGetInternalAuditReportById,
  useGetInternalAuditReportsRegister,
} from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import useExporter from './useExporter';

type Props = {
  activeTabId:
    | 'activities'
    | 'details'
    | 'findings'
    | 'impacts'
    | 'linkedItems';
  activityMode?: 'addActivity' | 'list' | 'updateActivity' | undefined;
};

const Page: FC<Props> = ({ activeTabId, activityMode = undefined }) => {
  const { t } = useTranslation(['common']);
  const internalAuditReportId = useGetGuidParam('assessmentId');
  const [exportItem, { loading: exporting }] = useExporter(
    internalAuditReportId
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const navigate = useNavigate();
  const parentUrl = useGetDetailParentPath(internalAuditReportId);
  const detailsPath = useGetDetailPath(internalAuditReportId);
  const { deleteInternalAuditReports, loading: deleteLoading } =
    useDeleteInternalAuditReports();

  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'internalAuditReports',
  });
  const { data, error, loading, refetch } = useGetInternalAuditReportById({
    queryArgs: { reportId: internalAuditReportId },
  });
  const { refetch: refetchInternalAuditsReportsRegister } =
    useGetInternalAuditReportsRegister({ queryArgs: {} });

  if (error) {
    throw error;
  }

  const internalAuditReport = data?.internal_audit_report[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.InternalAuditReport,
    parent: internalAuditReport,
    assessmentActivityMode: activityMode,
    assessmentMode: 'internal_audit_report',
    hrefRoot: detailsPath,
  });
  const onDelete = useDeleteResultNotification({
    entityName: st('entity'),
    asyncAction: async () => {
      if (!internalAuditReport) {
        return false;
      }
      await deleteInternalAuditReports([internalAuditReport.Id]);
      await refetchInternalAuditsReportsRegister();
      await navigate(parentUrl);
      refetch();

      return true;
    },
  });

  if (!loading && !internalAuditReport?.Id) {
    throw new PageNotFound(
      `Internal audit report with id ${internalAuditReportId} not found`
    );
  }

  const counter =
    internalAuditReport &&
    `(${getFriendlyId(
      Parent_Type_Enum.InternalAuditReport,
      internalAuditReport.SequentialId
    )})`;
  const fallbackTitle = st('fallback_title');

  // Object-level toolbar (CUS-165) — flag-gated. Relocates Export (download
  // group) + Delete (danger) into the toolbar's overflow (⋮) menu and hosts the
  // tab-settings (and Edit form, when bridged) openers in the "Edit" group. The
  // Save disc / Cancel X come from the active details form via the toolbar
  // bridge store. Export and Delete both keep the `delete:internal_audit_report`
  // gate resolved instance-level against `parentObject` — the legacy `actions`
  // block wraps both buttons in a single `<Permission>` with that same gate.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: internalAuditReport?.Title ?? fallbackTitle,
        counter: counter || undefined,
      },
      parentObject: internalAuditReport,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                permission: 'delete:internal_audit_report',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:internal_audit_report',
            // Hydration gate (ENG-616) — same as risks/assets.
            disabled: !internalAuditReport,
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [
      internalAuditReport,
      fallbackTitle,
      counter,
      t,
      st,
      formConfig,
      exportItem,
      exporting,
    ]
  );

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <Permission
          permission={'delete:internal_audit_report'}
          parentObject={internalAuditReport}
        >
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              iconName={'download'}
              disabled={exporting}
              onClick={exportItem}
            >
              {t('export.export')}
            </Button>
            <Button
              variant={'normal'}
              formAction={'none'}
              // Wait for the entity to hydrate. `onDelete` guards on
              // `if (!internalAuditReport) return false`, which surfaces as a
              // bare "Failed to save changes" with NO request issued, so a click
              // inside the hydration window looks like a broken delete
              // (ENG-616). Disabling also restores Playwright's actionability
              // wait.
              disabled={!internalAuditReport}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_button')}
            </Button>
          </SpaceBetween>
        </Permission>
      }
      meta={{
        title: fallbackTitle,
      }}
      title={internalAuditReport?.Title}
      counter={counter}
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={activeTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.InternalAuditReport}
        parent={internalAuditReport}
        disableSettings={objectToolbarEnabled}
      />

      <TabSettingsModal
        parentType={Parent_Type_Enum.InternalAuditReport}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={internalAuditReport}
      />

      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        <SpaceBetween size={'s'}>
          <Alert type={'warning'} dismissible={false}>
            {st('delete_modal_warning')}
          </Alert>
          {st('confirm_delete_message')}
        </SpaceBetween>
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
